import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, LayoutGrid, Lock } from "lucide-react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function CategoryPage() {
    const { slug } = useParams();
    const [categories, setCategories] = useState(null);
    const [memberDlg, setMemberDlg] = useState(null);

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data)).catch(() => setCategories([]));
    }, []);
    useEffect(() => { window.scrollTo(0, 0); }, [slug]);

    const cat = categories?.find((c) => c.slug === slug && !c.parent_id);
    const subs = cat ? categories.filter((c) => c.parent_id === cat.category_id) : [];

    return (
        <div className="min-h-screen bg-stone-950 text-stone-100" data-testid="category-page">
            <header className="sticky top-0 z-30 border-b border-stone-800/80 bg-stone-950/85 backdrop-blur">
                <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 sm:px-6">
                    <Link to="/" className="inline-flex items-center gap-2 rounded-full border border-stone-700 px-4 py-2 text-sm font-medium transition-colors hover:border-terra-500 hover:text-terra-400" data-testid="category-back-home">
                        <ArrowLeft className="h-4 w-4" /> Anasayfaya Dön
                    </Link>
                    <span className="ml-auto text-xs uppercase tracking-[0.3em] text-terra-500">ATA Angora</span>
                </div>
            </header>

            {!categories ? (
                <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6"><div className="h-64 animate-pulse rounded-3xl bg-stone-900" /></div>
            ) : !cat ? (
                <div className="mx-auto max-w-7xl px-4 py-24 text-center sm:px-6" data-testid="category-not-found">
                    <p className="font-display text-2xl font-bold">Kategori bulunamadı</p>
                    <p className="mt-2 text-sm text-stone-400">Aradığınız kategori kaldırılmış veya taşınmış olabilir.</p>
                    <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-full bg-terra-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-terra-700" data-testid="category-back-home-404">
                        <ArrowLeft className="h-4 w-4" /> Anasayfaya Dön
                    </Link>
                </div>
            ) : (
                <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
                    <div className="relative mb-10 overflow-hidden rounded-3xl border border-stone-800">
                        <img src={cat.image} alt={cat.name} className="h-56 w-full object-cover sm:h-72" />
                        <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/40 to-transparent" />
                        <div className="absolute bottom-0 p-6 sm:p-8">
                            <h1 className="font-display text-3xl font-bold sm:text-4xl" data-testid="category-title">{cat.name}</h1>
                            <p className="mt-1 text-sm text-stone-300">{subs.length} alt kategori • {cat.product_count} ürün</p>
                        </div>
                    </div>
                    <h2 className="mb-5 flex items-center gap-2 font-display text-lg font-bold">
                        <LayoutGrid className="h-5 w-5 text-terra-500" /> Alt Kategoriler
                    </h2>
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="category-subcategories">
                        {subs.map((s) => (
                            <button key={s.category_id} type="button" onClick={() => setMemberDlg(s.name)} className="group relative overflow-hidden rounded-2xl border border-stone-800 text-left transition-colors hover:border-terra-500/60" data-testid={`subcategory-card-${s.slug}`}>
                                <img src={s.image || cat.image} alt={s.name} className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-stone-950/10 to-transparent" />
                                <span className="absolute right-2 top-2 rounded-full bg-stone-950/80 p-1.5 text-stone-300 opacity-0 transition-opacity group-hover:opacity-100"><Lock className="h-3 w-3" /></span>
                                <p className="absolute bottom-0 w-full p-3 text-center font-display text-sm font-bold leading-tight">{s.name}</p>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <Dialog open={!!memberDlg} onOpenChange={(o) => { if (!o) setMemberDlg(null); }}>
                <DialogContent className="max-w-md border-stone-700 bg-stone-900 text-stone-100" data-testid="category-member-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 font-display"><Lock className="h-5 w-5 text-terra-500" /> Üyelere Özel</DialogTitle>
                        <DialogDescription className="sr-only">Bu kategorideki ürünler yalnızca üyelere açıktır</DialogDescription>
                    </DialogHeader>
                    <p className="text-sm leading-relaxed text-stone-300">
                        <span className="font-semibold text-white">"{memberDlg}"</span> kategorisindeki ürünleri, fiyatları ve tedarikçileri görmek için ücretsiz üye olun.
                    </p>
                    <div className="mt-2 grid gap-2">
                        <Link to="/?kayit=magaza" className="rounded-xl bg-terra-600 px-4 py-2.5 text-center text-sm font-semibold text-white transition-colors hover:bg-terra-700" data-testid="member-register-buyer">Mağaza Kaydı Oluştur</Link>
                        <Link to="/?kayit=tedarikci" className="rounded-xl border border-stone-600 px-4 py-2.5 text-center text-sm font-semibold transition-colors hover:border-terra-500" data-testid="member-register-supplier">Tedarikçi Kaydı Oluştur</Link>
                        <Link to="/?giris=1" className="py-1 text-center text-xs text-stone-400 underline-offset-2 transition-colors hover:text-stone-200 hover:underline" data-testid="member-login">Zaten üye misiniz? Giriş yapın</Link>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
